import { useEffect, useState } from "react";
import { FileText, Loader2, Printer, Key, Lock, Sparkles, RefreshCw } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Premium worksheet generator embedded in the lesson-help dialog.
 * - Only enabled when the lesson's student is on the Premium plan (backend returns 402 otherwise).
 * - Persists the latest worksheet per lesson; re-open loads it via GET.
 */
export default function WorksheetPanel({ lesson, isPremium }) {
  const [worksheet, setWorksheet] = useState(null);
  const [busy, setBusy] = useState(false);
  const [count, setCount] = useState("8");
  const [difficulty, setDifficulty] = useState("grade-level");

  // Load existing worksheet on mount / lesson change
  useEffect(() => {
    let cancelled = false;
    if (!lesson?.id) return;
    (async () => {
      try {
        const { data } = await api.get(`/lessons/${lesson.id}/worksheet`);
        if (!cancelled) setWorksheet(data);
      } catch (e) {
        if (!cancelled) setWorksheet(null);
      }
    })();
    return () => { cancelled = true; };
  }, [lesson?.id]);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/lessons/${lesson.id}/worksheet`, {
        question_count: parseInt(count, 10),
        difficulty,
        include_answer_key: true,
      });
      setWorksheet(data);
      toast.success("Worksheet ready");
    } catch (e) {
      const detail = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  };

  const openPdf = (withAnswers) => {
    if (!worksheet?.id) return;
    const url = `${BACKEND_URL}/api/worksheets/${worksheet.id}/print${withAnswers ? "?with_answers=true" : ""}`;
    window.open(url, "_blank", "noopener");
  };

  if (!isPremium) {
    return (
      <div className="rounded-xl border border-dashed border-amber-300 bg-amber-50/40 p-4" data-testid="worksheet-locked">
        <div className="flex items-start gap-3">
          <div className="grid place-items-center w-9 h-9 rounded-lg bg-white ring-1 ring-amber-200 text-amber-600 shrink-0">
            <Lock className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs uppercase tracking-[0.15em] font-semibold text-amber-800 flex items-center gap-1.5">
              <FileText className="w-3.5 h-3.5" /> Worksheet generator
            </div>
            <div className="mt-1 text-sm text-stone-700">
              Auto-generated printable worksheets + answer keys are part of <b>Premium</b>. Upgrade this student&apos;s subscription to unlock.
            </div>
            <a href="/dashboard/billing" className="mt-2 inline-block text-sm text-amber-700 hover:text-amber-900 font-semibold" data-testid="worksheet-upgrade-link">
              Upgrade to Premium →
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-indigo-200 bg-indigo-50/40 p-4" data-testid="worksheet-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-xs uppercase tracking-[0.15em] font-semibold text-indigo-800 flex items-center gap-1.5">
          <FileText className="w-3.5 h-3.5" /> Printable worksheet
          <span className="ml-1 rounded-full bg-indigo-500 text-white text-[9px] px-1.5 py-0.5 uppercase tracking-wider">Premium</span>
        </div>
        {worksheet && (
          <span className="text-[11px] text-stone-500">
            {worksheet.questions?.length || 0} q · {worksheet.difficulty}
          </span>
        )}
      </div>

      {!worksheet && !busy && (
        <div className="mt-3 grid gap-2 md:grid-cols-[1fr,1fr,auto]">
          <Select value={count} onValueChange={setCount}>
            <SelectTrigger className="h-10 rounded-lg" data-testid="worksheet-count-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="5">5 questions</SelectItem>
              <SelectItem value="8">8 questions</SelectItem>
              <SelectItem value="10">10 questions</SelectItem>
              <SelectItem value="15">15 questions</SelectItem>
            </SelectContent>
          </Select>
          <Select value={difficulty} onValueChange={setDifficulty}>
            <SelectTrigger className="h-10 rounded-lg" data-testid="worksheet-difficulty-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="easy">Easy (review)</SelectItem>
              <SelectItem value="grade-level">Grade level</SelectItem>
              <SelectItem value="challenge">Challenge</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={generate} className="rounded-full bg-indigo-500 hover:bg-indigo-600 text-white h-10" data-testid="worksheet-generate-btn">
            <Sparkles className="w-4 h-4 mr-2" /> Generate
          </Button>
        </div>
      )}

      {busy && (
        <div className="mt-3 flex items-center gap-2 text-sm text-stone-500" data-testid="worksheet-busy">
          <Loader2 className="w-4 h-4 animate-spin" /> Writing your worksheet…
        </div>
      )}

      {worksheet && !busy && (
        <div className="mt-3 space-y-3" data-testid="worksheet-ready">
          <div className="rounded-lg bg-white ring-1 ring-stone-200 p-3">
            <div className="font-display font-semibold text-stone-900">{worksheet.title}</div>
            {worksheet.instructions && (
              <div className="mt-1 text-xs text-stone-600 leading-relaxed">{worksheet.instructions}</div>
            )}
            <ul className="mt-2 text-xs text-stone-500 list-disc list-inside space-y-0.5">
              {(worksheet.questions || []).slice(0, 3).map((q, i) => (
                <li key={i} className="truncate" data-testid={`worksheet-preview-${i}`}>{q.n}. {q.prompt}</li>
              ))}
              {(worksheet.questions || []).length > 3 && <li className="text-stone-400">…and {worksheet.questions.length - 3} more</li>}
            </ul>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              onClick={() => openPdf(false)}
              className="rounded-full bg-stone-900 hover:bg-stone-800 text-white"
              data-testid="worksheet-print-student"
            >
              <Printer className="w-4 h-4 mr-2" /> Print worksheet
            </Button>
            <Button
              onClick={() => openPdf(true)}
              variant="outline"
              className="rounded-full border-indigo-300 text-indigo-700 hover:bg-indigo-50"
              data-testid="worksheet-print-key"
            >
              <Key className="w-4 h-4 mr-2" /> Answer key
            </Button>
            <Button
              onClick={generate}
              variant="ghost"
              className="rounded-full text-stone-500 hover:text-stone-800"
              data-testid="worksheet-regenerate"
            >
              <RefreshCw className="w-3.5 h-3.5 mr-2" /> Regenerate
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
